/**
 * Configuração de runtime do softphone (por cliente), lida de `window.__PM_CONFIG__`
 * (mesmo `public/config.js` do resto do app) com fallback para `VITE_*`.
 *
 *  - `softphoneExternalPrefix` / `VITE_SOFTPHONE_EXTERNAL_PREFIX`: prefixo de rota externa
 *    acrescentado a números externos na discagem manual (lab: "0", ver `extensions_lab.conf`).
 *  - `softphoneExtensionMaxLength` / `VITE_SOFTPHONE_EXTENSION_MAX_LENGTH`: até quantos dígitos
 *    um destino é tratado como ramal/fila (sem prefixo). Padrão 5.
 *  - `softphoneWsServer` / `VITE_SOFTPHONE_WS_SERVER`: sobrescreve o `wsServer` do ramal
 *    (útil em lab/proxy). Normalmente vazio: vale o `wsServer` vindo de `phoneByLogin`.
 *  - `softphoneHeartbeatSeconds`: intervalo do heartbeat periódico (padrão 30s; o backend marca
 *    "Connection lost" após `chamber.maximumTimeWithoutStatusUpdateInSeconds` = 60s).
 */

type SoftphoneRuntimeConfig = {
  softphoneExternalPrefix?: string;
  softphoneExtensionMaxLength?: string | number;
  softphoneWsServer?: string;
  softphoneHeartbeatSeconds?: string | number;
};

function rc(): SoftphoneRuntimeConfig {
  if (typeof window === "undefined") return {};
  const cfg = (window as unknown as { __PM_CONFIG__?: SoftphoneRuntimeConfig }).__PM_CONFIG__;
  return cfg && typeof cfg === "object" ? cfg : {};
}

function pick(...values: (string | number | undefined | null)[]): string | undefined {
  for (const v of values) {
    if (v == null) continue;
    const s = String(v).trim();
    if (!s || /^\$\{.*\}$/.test(s)) continue;
    return s;
  }
  return undefined;
}

const cfg = rc();
const env = import.meta.env as Record<string, string | undefined>;

export const SOFTPHONE_EXTERNAL_PREFIX = pick(cfg.softphoneExternalPrefix, env.VITE_SOFTPHONE_EXTERNAL_PREFIX) ?? "";

export const SOFTPHONE_EXTENSION_MAX_LENGTH = Math.max(
  2,
  Number(pick(cfg.softphoneExtensionMaxLength, env.VITE_SOFTPHONE_EXTENSION_MAX_LENGTH) ?? 5) || 5,
);

export const SOFTPHONE_WS_SERVER_OVERRIDE = pick(cfg.softphoneWsServer, env.VITE_SOFTPHONE_WS_SERVER);

/** Entre 10s e 50s — sempre abaixo dos 60s do janitor do backend. */
export const SOFTPHONE_HEARTBEAT_MS =
  Math.min(50, Math.max(10, Number(pick(cfg.softphoneHeartbeatSeconds) ?? 30) || 30)) * 1000;

/** Registro SIP: o Asterisk limita ao `maximum_expiration` (7200s); renovação é automática. */
export const SIP_REGISTER_EXPIRES = 600;

/**
 * Normaliza o destino digitado para o formato discável:
 * remove máscara, mantém dígitos/*#/+, e aplica o prefixo externo em números longos.
 */
export function normalizeDialTarget(raw: string): { target: string; external: boolean } {
  const cleaned = raw.trim().replace(/[\s().-]/g, "");
  const digitsOnly = cleaned.replace(/^\+/, "");
  const external = /^\d+$/.test(digitsOnly) && digitsOnly.length > SOFTPHONE_EXTENSION_MAX_LENGTH;
  if (!external) return { target: cleaned, external: false };
  const prefix = SOFTPHONE_EXTERNAL_PREFIX;
  if (!prefix || digitsOnly.startsWith(prefix)) return { target: digitsOnly, external: true };
  return { target: `${prefix}${digitsOnly}`, external: true };
}

export function isValidDialTarget(raw: string): boolean {
  const cleaned = raw.trim().replace(/[\s().-]/g, "");
  return /^\+?[0-9*#]{1,32}$/.test(cleaned);
}
